import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, TextField,
  useTheme, IconButton, Tooltip, Box,
  Typography
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useSelector } from 'react-redux';
import { State } from '../../state/reducers';
import dayjs from 'dayjs';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from 'react-beautiful-dnd';
import { getVehiclesFromDb } from '../../api/getVehiclesFromDb';
import { vehicleCsvFields } from './vehicleCsvFields';

const initialFieldList = vehicleCsvFields.map(({ key, label, enabled }) => ({ key, label, enabled }));
const baseUrl = window.location.origin;

const GenerateCsvModal = () => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [enabledFields, setEnabledFields] = useState(initialFieldList.filter(f => f.enabled));
  const [disabledFields, setDisabledFields] = useState(initialFieldList.filter(f => !f.enabled));
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);

  const selectedSystem = useSelector((state: State) => state.systemState.selectedSystem);
  const theme = useTheme();

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceList = source.droppableId === 'enabled' ? [...enabledFields] : [...disabledFields];
    const destList = destination.droppableId === 'enabled' ? [...enabledFields] : [...disabledFields];

    const [movedItem] = sourceList.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      sourceList.splice(destination.index, 0, movedItem);
      if (source.droppableId === 'enabled') setEnabledFields(sourceList);
      else setDisabledFields(sourceList);
    } else {
      destList.splice(destination.index, 0, movedItem);
      if (source.droppableId === 'enabled') {
        setEnabledFields(sourceList);
        setDisabledFields(destList.sort((a, b) => a.label.localeCompare(b.label)));
      } else {
        setDisabledFields(sourceList);
        setEnabledFields(destList);
      }
    }
  };

  const generateCSV = async () => {
    setGenerating(true);

    try {
      const perPage = 100;
      let totalVehicles = 0;
      let allVehicles: any[] = [];

      setCurrentPage(1); // first fetch

      const firstResponse = await getVehiclesFromDb({
        page: 1,
        per_page: perPage,
        system_code: selectedSystem,
        start_date: startDate,
        end_date: endDate,
      });

      allVehicles = firstResponse.vehicles;
      totalVehicles = firstResponse.total;

      const total = Math.ceil(totalVehicles / perPage);
      setTotalPages(total);

      // Fetch remaining pages
      for (let page = 2; page <= total; page++) {
        setCurrentPage(page); // track current page

        const response = await getVehiclesFromDb({
          page,
          per_page: perPage,
          system_code: selectedSystem,
          start_date: startDate,
          end_date: endDate,
        });

        allVehicles.push(...response.vehicles);
      }


      const csvHeaders = enabledFields.map(f => f.label);

      const csvRows = allVehicles.map(v =>
        enabledFields.map(field => {
          const extractor = vehicleCsvFields.find(f => f.key === field.key)?.extractor;
          return extractor ? extractor(v) : '';
        })
      );

      const csvContent = [csvHeaders, ...csvRows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `vehicles_export_${Date.now()}.csv`);
    } catch (err) {
      console.error('CSV generation error:', err);
      alert("Error generating CSV.");
    } finally {
      setGenerating(false);
      setOpen(false);
    }
  };


  const csvUrl = `${baseUrl}/csv-download?client=${selectedSystem}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&fields=${enabledFields.map(f => f.key).join(',')}`;

  return (
    <>
      <Tooltip title="Generate CSV" placement="right" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: 5,
            m: 1,
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.primary,
            },
          }}
        >
          <DescriptionIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate CSV</DialogTitle>

        <DialogContent>
          {generating ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
            >
              <CircularProgress />
              <Typography variant="h6" mt={3}>
                Generating CSV...
              </Typography>
              {currentPage && totalPages && (
                <Typography variant="body2" mt={1}>
                  Downloading page {currentPage} of {totalPages}
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />

              <DragDropContext onDragEnd={handleDragEnd}>
                <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Enabled Fields (Drag to reorder):</div>
                    <Droppable droppableId="enabled">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            maxHeight: 360,
                            overflowY: 'auto',
                            padding: 12,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                            backgroundColor: theme.palette.background.default,
                          }}
                        >
                          {enabledFields.map((field, index) => (
                            <Draggable key={field.key} draggableId={field.key} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    padding: 6,
                                    margin: '8px 0',
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 4,
                                    fontSize: '14px',
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  {field.label}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Disabled Fields:</div>
                    <Droppable droppableId="disabled">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            maxHeight: 360,
                            overflowY: 'auto',
                            padding: 12,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                            backgroundColor: theme.palette.background.default,
                          }}
                        >
                          {disabledFields.map((field, index) => (
                            <Draggable key={field.key} draggableId={field.key} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    padding: 6,
                                    margin: '8px 0',
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 4,
                                    fontSize: '14px',
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  {field.label}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              </DragDropContext>

              {/* 👇 Direct CSV link preview and button */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Box sx={{ fontWeight: 600, mb: 1 }}>Direct CSV Download Link:</Box>
                <Box
                  sx={{
                    fontSize: '0.8rem',
                    wordBreak: 'break-word',
                    color: theme.palette.text.secondary,
                    mb: 1,
                  }}
                >
                  {csvUrl}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(csvUrl);
                      alert("CSV link copied to clipboard!");
                    } catch (err) {
                      console.error("Failed to copy link:", err);
                      alert("Failed to copy link. Try again.");
                    }
                  }}
                >
                  Copy Link
                </Button>

              </Box>
            </>
          )}

        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            disabled={generating}
            sx={{
              color: theme.palette.error.main,
              border: `1px solid ${theme.palette.error.main}`,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.palette.error.light,
                borderColor: theme.palette.error.dark,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={generateCSV}
            disabled={generating}
            sx={{
              color: theme.palette.warning.contrastText,
              backgroundColor: theme.palette.warning.main,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.palette.warning.dark,
              },
            }}
          >
            {generating ? <CircularProgress size={20} sx={{ color: theme.palette.warning.contrastText }} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
};

export default GenerateCsvModal;
